import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Grow,
  Button,
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon, 
  ExpandLess as ExpandLessIcon 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { HOME_STYLES, generateDynamicCards, shuffleArray, sortCardsByPopularity } from '../constants/homeConstants';
import usePermissions from '../../hooks/usePermissions';
import { useMostVisitedPages } from '../../hooks/usePageTracking';

const QuickAccessSection = () => {
  const navigate = useNavigate();
  const { hasModule } = usePermissions();
  const { mostVisitedPages, hasSufficientData } = useMostVisitedPages();
  const [expanded, setExpanded] = useState(false);

  const dynamicCards = generateDynamicCards(hasModule, mostVisitedPages);
  
  const cardsToShow = hasSufficientData 
    ? sortCardsByPopularity(dynamicCards)
    : shuffleArray(dynamicCards);

  const INITIAL_CARDS_COUNT = 8;
  
  const visibleCards = expanded ? cardsToShow : cardsToShow.slice(0, INITIAL_CARDS_COUNT);
  const hasMoreCards = cardsToShow.length > INITIAL_CARDS_COUNT;

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, color: 'text.primary' }}>
        Acceso Rápido
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        {hasSufficientData 
          ? 'Páginas ordenadas por frecuencia de uso' 
          : 'Páginas disponibles según tus permisos'
        }
        {hasMoreCards && (
          <span style={{ marginLeft: '8px', opacity: 0.7 }}>
            ({expanded ? 'Mostrando todas' : `Mostrando ${INITIAL_CARDS_COUNT} de ${cardsToShow.length}`})
          </span>
        )}
      </Typography>
      
      <Grid container spacing={3}>
        {visibleCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`${card.title}-${index}`}>
            <Grow in timeout={800 + index * 200}>
              <Card
                sx={{
                  ...HOME_STYLES.card,
                  background: card.color,
                  position: 'relative',
                }}
                onClick={() => navigate(card.path)}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  {card.visitCount > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        px: 1,
                        py: 0.5,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {card.visitCount} {card.visitCount === 1 ? 'visita' : 'visitas'}
                    </Box>
                  )}
                  
                  <Box sx={{ mb: 2 }}>
                    {React.cloneElement(card.icon, { sx: { fontSize: 48, color: 'white' } })}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {card.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grow>
          </Grid>
        ))}
      </Grid>

      {hasMoreCards && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleToggleExpand}
            startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              color: 'primary.main',
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white',
              },
              px: 4,
              py: 1.5,
            }}
          >
            {expanded 
              ? 'Mostrar menos' 
              : `Ver ${cardsToShow.length - INITIAL_CARDS_COUNT} más`
            }
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default QuickAccessSection;
